import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { Editor as TiptapEditor, JSONContent } from '@tiptap/react';
import { useUser } from '@clerk/react';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { Editor } from '../editor/Editor';
import { syncCards } from '../editor/cardSync';
import { CardGenerationDialog, type GenerationContext } from '../components/CardGenerationDialog';
import { ExtraReviewMenu } from '../components/ExtraReviewMenu';
import { ShareDialog } from '../components/ShareDialog';
import { FileText, Sparkles } from '../components/icons';
import { mockBacklinks } from '../data/mock';
import {
  useCards,
  useCreatePage,
  usePage,
  usePageChildren,
  useSavePage,
  type ApiCard,
} from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';

const AUTOSAVE_MS = 800;

function countCloze(doc: JSONContent): number {
  let n = 0;
  const walk = (node: JSONContent) => {
    if (node.type === 'cardBlock' && node.attrs?.cardType === 'cloze') n += 1;
    node.content?.forEach(walk);
  };
  walk(doc);
  return n;
}

export function PageRoute() {
  const { id } = useParams();
  if (!id) return <NoIdFallback />;
  // Remount on id change so local draft state can't bleed between pages.
  return <PageEditor key={id} pageId={id} />;
}

function NoIdFallback() {
  const { data: rootPages, isLoading } = usePageChildren(null);
  if (isLoading) return <LoadingBlock />;
  if (!rootPages || rootPages.length === 0) {
    return (
      <div style={{ padding: 40, color: colors.ink3 }}>
        No pages yet. Create one from the sidebar.
      </div>
    );
  }
  return <Navigate to={`/pages/${rootPages[0]!.id}`} replace />;
}

function LoadingBlock() {
  return (
    <div style={{ padding: 40, color: colors.ink3, fontSize: 13 }}>…</div>
  );
}

function PageEditor({ pageId }: { pageId: string }) {
  const { t } = useTranslation('page');
  const { t: tCommon } = useTranslation('common');
  const { user } = useUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = usePage(pageId);
  const savePage = useSavePage();
  const createPage = useCreatePage();

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [doc, setDoc] = useState<JSONContent | null>(null);
  const [initialDoc, setInitialDoc] = useState<JSONContent | null>(null);
  const [genContext, setGenContext] = useState<GenerationContext | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const editorRef = useRef<TiptapEditor | null>(null);

  // Hydrate local state when the query resolves or the id changes.
  useEffect(() => {
    if (!data) return;
    setTitle(data.page.title ?? '');
    setSubtitle(data.page.subtitle ?? '');
    setDoc(data.page.content);
    setInitialDoc(data.page.content);
  }, [data, pageId]);

  // Debounced autosave. Fires only when the local draft diverges from the server copy.
  const saveRef = useRef(savePage.mutateAsync);
  saveRef.current = savePage.mutateAsync;
  useEffect(() => {
    if (!data) return;
    const dirty =
      title !== (data.page.title ?? '') ||
      subtitle !== (data.page.subtitle ?? '') ||
      JSON.stringify(doc) !== JSON.stringify(data.page.content);
    if (!dirty) return;
    const handle = setTimeout(async () => {
      try {
        await saveRef.current({
          id: pageId,
          title,
          subtitle: subtitle || null,
          content: doc ?? undefined,
        });
        if (doc) {
          await syncCards(pageId, doc);
          qc.invalidateQueries({ queryKey: ['cards', pageId] });
        }
      } catch (err) {
        console.error('autosave failed', err);
      }
    }, AUTOSAVE_MS);
    return () => clearTimeout(handle);
  }, [title, subtitle, doc, data, pageId, qc]);

  const clozeCount = useMemo(() => (doc ? countCloze(doc) : 0), [doc]);
  const { data: cards = [] } = useCards(pageId);

  if (error) {
    return (
      <div style={{ padding: 40, color: colors.bad }}>
        {tCommon('error_generic')}: {(error as Error).message}
      </div>
    );
  }
  if (isLoading || !data || !initialDoc) return <LoadingBlock />;

  const canEdit = data.access.role === 'owner' || data.access.role === 'editor';

  const openFromSelection = (text: string) => setGenContext({ source: text, scope: 'selection' });
  const openFromPage = () => {
    const text = editorRef.current?.getText() ?? '';
    if (text.trim())
      setGenContext({ source: `${title}\n\n${text}`, scope: 'page', suggestedName: title });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 288px', minHeight: '100vh' }}>
      <div
        style={{
          padding: '32px 56px 60px',
          position: 'relative',
          overflowY: 'auto',
          background: colors.surface,
        }}
      >
        <Breadcrumb title={title || tCommon('empty_state')} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}>
          <SaveStatus isSaving={savePage.isPending} />
          <button
            onClick={() => setShareOpen(true)}
            style={shareBtn}
          >
            {tCommon('share')}
          </button>
        </div>

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('title_placeholder')}
          readOnly={!canEdit}
          style={titleInput}
        />
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder={t('subtitle_placeholder')}
          readOnly={!canEdit}
          style={subtitleInput}
        />

        <div style={metaRow}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={avatarXs}>
              {(user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? '?').slice(0, 1).toUpperCase()}
            </span>
            {user?.fullName ?? user?.primaryEmailAddress?.emailAddress}{' '}
            · {t('meta_last_edited', { when: new Date(data.page.updatedAt).toLocaleString() })}
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.06em' }}>
            {t('cards_count', { count: clozeCount, total: cards.length })}
          </span>
        </div>

        <Editor
          key={pageId}
          initialContent={initialDoc}
          onChange={setDoc}
          onMount={(e) => {
            editorRef.current = e;
          }}
          onGenerateFromSelection={openFromSelection}
          onCreateSubpage={async (childTitle) => {
            const created = await createPage.mutateAsync({
              title: childTitle,
              parentPageId: pageId,
            });
            return { id: created.id, title: created.title };
          }}
          onOpenPage={(id) => navigate(`/pages/${id}`)}
        />
      </div>

      <RightPanel
        pageId={pageId}
        cards={cards}
        clozeCount={clozeCount}
        canEdit={canEdit}
        onGenerate={openFromPage}
      />

      <CardGenerationDialog
        open={genContext !== null}
        context={genContext}
        onClose={() => setGenContext(null)}
      />

      <ShareDialog
        open={shareOpen}
        pageId={pageId}
        pageTitle={title}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}

function SaveStatus({ isSaving }: { isSaving: boolean }) {
  return (
    <span
      style={{
        fontVariantNumeric: 'tabular-nums',
        fontSize: 11,
        color: colors.ink3,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {isSaving ? 'Saving…' : 'Saved'}
    </span>
  );
}

function Breadcrumb({ title }: { title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        fontSize: 12,
        color: colors.ink3,
        marginBottom: 20,
      }}
    >
      <FileText size={11} />
      <span style={{ color: colors.ink2 }}>{title}</span>
    </div>
  );
}

function RightPanel({
  pageId,
  cards,
  clozeCount,
  canEdit,
  onGenerate,
}: {
  pageId: string;
  cards: ApiCard[];
  clozeCount: number;
  canEdit: boolean;
  onGenerate: () => void;
}) {
  const { t } = useTranslation('page');
  return (
    <aside
      style={{
        background: colors.surface2,
        borderLeft: `1px solid ${colors.line}`,
        padding: '22px 18px',
        overflowY: 'auto',
        fontSize: 13,
      }}
    >
      <PanelHeading first>{t('right_panel.cards_in_page')}</PanelHeading>
      <div
        style={{
          padding: '14px 16px',
          background: colors.surface,
          border: `1px solid ${colors.line2}`,
          borderRadius: 10,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            letterSpacing: '-0.02em',
            fontSize: 40,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
          }}
        >
          {cards.length}
          <em style={{ fontStyle: 'italic', fontSize: 14, color: colors.ink3 }}>
            {clozeCount ? t('right_panel.cloze_in_doc', { count: clozeCount }) : t('right_panel.total_cards')}
          </em>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <Link
            to={`/review/${pageId}`}
            style={{ ...reviewBtn, flex: 1, textAlign: 'center', textDecoration: 'none', display: 'inline-block' }}
          >
            {t('right_panel.review_this_page')}
          </Link>
          <ExtraReviewMenu pageId={pageId} />
        </div>
        <button onClick={onGenerate} style={generateBtn} disabled={!canEdit}>
          <Sparkles size={12} />
          {t('right_panel.generate_cards')}
        </button>
      </div>

      <PanelHeading>{t('right_panel.card_list')}</PanelHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {cards.length === 0 ? (
          <div style={{ fontSize: 11.5, color: colors.ink3, fontStyle: 'italic', padding: '4px 0' }}>
            {t('right_panel.empty_cards')}
          </div>
        ) : (
          cards.map((c, i) => <CardMini key={c.id} n={i + 1} card={c} />)
        )}
      </div>

      <PanelHeading>{t('right_panel.backlinks')}</PanelHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {mockBacklinks.map((b) => (
          <div key={b.id} style={backlink}>
            <FileText size={11} style={{ color: colors.ink3 }} />
            {b.title}
            <span
              style={{
                marginLeft: 'auto',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
                color: colors.ink3,
              }}
            >
              {b.count}
            </span>
          </div>
        ))}
      </div>
    </aside>
  );
}

function PanelHeading({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <h4
      style={{
        fontVariantNumeric: 'tabular-nums',
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: colors.ink3,
        fontWeight: 500,
        marginBottom: 12,
        marginTop: first ? 0 : 22,
      }}
    >
      {children}
    </h4>
  );
}

function CardMini({ n, card }: { n: number; card: ApiCard }) {
  const due = card.dueAt ? new Date(card.dueAt).getTime() <= Date.now() : true;
  const statusColor = card.intervalDays == null ? colors.warn : due ? colors.accent : colors.good;
  const typeColor =
    card.type === 'mcq' ? colors.good : card.type === 'cloze' ? colors.warn : colors.accent;
  return (
    <div
      style={{
        padding: '9px 11px 9px 26px',
        background: colors.surface,
        border: `1px solid ${colors.line}`,
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.4,
        color: colors.ink3,
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 10,
          left: 9,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 9.5,
          color: typeColor,
        }}
      >
        {String(n).padStart(2, '0')}
      </span>
      <div
        style={{
          fontWeight: 500,
          color: colors.ink,
          fontSize: 12.5,
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {card.front || '(empty)'}
      </div>
      <span
        style={{
          position: 'absolute',
          top: 10,
          right: 9,
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: statusColor,
        }}
      />
    </div>
  );
}

const shareBtn: React.CSSProperties = {
  marginLeft: 'auto',
  background: colors.surface,
  border: `1px solid ${colors.line2}`,
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 12.5,
  color: colors.ink2,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const reviewBtn: React.CSSProperties = {
  width: '100%',
  background: colors.ink,
  color: colors.surface,
  border: 'none',
  padding: 9,
  borderRadius: 7,
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 500,
  cursor: 'pointer',
};

const generateBtn: React.CSSProperties = {
  width: '100%',
  background: 'transparent',
  color: colors.accent,
  border: `1px solid ${colors.accentSoft}`,
  padding: 9,
  borderRadius: 7,
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 500,
  cursor: 'pointer',
  marginTop: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

const backlink: React.CSSProperties = {
  padding: '7px 10px',
  background: colors.surface,
  border: `1px solid ${colors.line}`,
  borderRadius: 5,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  color: colors.ink2,
  fontSize: 12,
};

const titleInput: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontWeight: 600,
  letterSpacing: '-0.025em',
  fontSize: 54,
  lineHeight: 1,
  marginBottom: 10,
  fontFamily: 'inherit',
  color: colors.ink,
};

const subtitleInput: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  fontStyle: 'italic',
  fontSize: 20,
  color: colors.ink3,
  marginBottom: 28,
  fontFamily: 'inherit',
};

const metaRow: React.CSSProperties = {
  display: 'flex',
  gap: 18,
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 12,
  color: colors.ink3,
  paddingBottom: 20,
  marginBottom: 28,
  borderBottom: `1px solid ${colors.line}`,
};

const avatarXs: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${colors.accent}, ${colors.warn})`,
  color: colors.surface,
  fontWeight: 600,
  letterSpacing: '-0.02em',
  fontSize: 11,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 6,
};
